import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';

const CANONICAL = 'https://e-asy.es/erp-para-ong';

const SERVICE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Software de gestión y ERP para ONG y entidades sociales',
  description:
    'Digitalización de la gestión en ONG: expedientes, proyectos sociales, subvenciones y trazabilidad, con ERP adaptado y acompañamiento.',
  url: CANONICAL,
  serviceType: 'Consultoría e implantación de software de gestión para ONG',
  provider: {
    '@type': 'Organization',
    name: 'E-ASY',
    url: 'https://e-asy.es',
  },
  areaServed: { '@type': 'Country', name: 'España' },
};

const FAQ = [
  {
    question: '¿Trabajan con ONG?',
    answer:
      'Sí. Acompañamos a asociaciones y entidades del tercer sector en ordenar la gestión con software adaptado, respetando cómo documentas proyectos y ayudas. También tenemos el área Social de Vulpin 360 pensada para este contexto.',
  },
  {
    question: '¿Sirve para gestión de expedientes en ONG?',
    answer:
      'Cuando hablamos de expedientes no solo archivamos PDFs: definimos estados, responsables, plazos y vínculo con proyectos o subvenciones para que el equipo vea el mismo expediente en curso, sin carpetas sueltas en el disco.',
  },
  {
    question: '¿Qué significa digitalización ONG sin perder el enfoque social?',
    answer:
      'No se trata de tecnología por moda, sino de quitar carga administrativa repetitiva para que el tiempo vuelva a personas y programas. Eso implica formación clara y un sistema que se entienda sin ser informáticos.',
  },
  {
    question: '¿Cómo es el proceso de implantación?',
    answer:
      'Sesiones de diagnóstico con quienes llevan administración y proyectos, priorización de lo que más duele (informes, duplicidad de datos, trazabilidad) y arranque por fases para no colapsar al voluntariado ni al personal.',
  },
  {
    question: '¿Trabajáis con entidades de fuera de Canarias?',
    answer:
      'Sí. La implantación y el seguimiento se hacen en remoto con entidades de toda España. La oficina está en Tenerife y la atención presencial se concentra en Canarias.',
  },
];

const ErpParaOngPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Software de gestión para ONG y ERP para asociaciones | E-ASY"
    description="Software de gestión para ONG y ERP para asociaciones: expedientes, proyectos sociales y trazabilidad. Implantación en remoto para entidades de toda España, desde nuestra sede en Tenerife."
    keywords="software gestión ONG, ERP para asociaciones, gestión de expedientes ONG, digitalización ONG, software para proyectos sociales, consultoría ONG España"
    canonical={CANONICAL}
    h1="Software de gestión para ONG: menos papeleo, más trazabilidad"
    lede="Las ONG y asociaciones acumulan proyectos, ayudas y documentación. Si eso vive en carpetas sueltas y hojas compartidas, cuesta rendir cuentas y coordinar equipos. Nosotros lo ordenamos con herramientas que se adaptan a tu organización."
    faqItems={FAQ}
    serviceJsonLd={SERVICE_LD}
    serviceJsonLdId="jsonld-service-erp-ong"
  >
    <h2>Cuando necesitas trazabilidad en ONG sin complicar al voluntariado</h2>
    <p>
      Informes a fundaciones, seguimiento de beneficiarios o proyectos,
      auditorías internas: todo es más sencillo cuando los datos viven en un
      mismo sitio y cada cambio queda registrado. Eso es lo que busca buena
      parte de la <strong>digitalización ONG</strong>: claridad, no más
      software oscuro.
    </p>

    <h2>ERP para asociaciones y software para proyectos sociales</h2>
    <p>
      Un <strong>ERP para asociaciones</strong> no tiene por qué ser el mismo
      que el de una fábrica. Priorizamos módulos y flujos que encajan con
      subvenciones, memorias, indicadores y <strong>gestión de expedientes ONG</strong>.
      Nuestro producto{' '}
      <Link to="/vulpin">Vulpin Suite</Link> incluye el área Social para este tipo de
      entidad; si hace falta, lo extendemos o conectamos con lo que ya usas.
    </p>

    <h2>Integrar donaciones, contabilidad o herramientas externas</h2>
    <p>
      Muchas entidades siguen usando herramientas de cobro, banca o
      contabilidad externa. Podemos{' '}
      <Link to="/integracion-sistemas">integrar sistemas</Link> para que los
      datos entren una vez y los informes salgan coherentes, sin pegar tablas a
      mano cada mes.
    </p>

    <h2>Lo repetitivo en administración</h2>
    <p>
      Recordatorios, estados de expediente, avisos a responsables: en{' '}
      <Link to="/vulpin">Vulpin Suite</Link>{' '}
      esos flujos van dentro del producto. Lo definimos contigo; no es un
      servicio de automatización aparte.
    </p>

    <h2>Software de gestión para ONG en toda España</h2>
    <p>
      Implantamos y acompañamos en remoto a entidades de cualquier comunidad.
      La sede está en Tenerife. El detalle está en{' '}
      <Link to="/donde-trabajamos">dónde trabajamos</Link>. Si el proyecto es
      local, puedes ver la{' '}
      <Link to="/consultoria-tecnologica-tenerife">
        consultoría tecnológica en Tenerife
      </Link>
      .
    </p>

    <div className="seo-landing-cta-row">
      <Link to="/tercer-sector" className="seo-landing-cta">
        Ver enfoque tercer sector
      </Link>
      <Link to="/contacto" className="seo-landing-cta">
        Hablar sobre mi gestión
      </Link>
    </div>
  </SeoLandingLayout>
);

export default ErpParaOngPage;
